import { useParams, Link } from "react-router-dom";
import { useEffect } from "react";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/feature/Breadcrumb";
import MovieCard from "@/components/feature/MovieCard";
import SeoHead from "@/components/feature/SeoHead";
import { movies, categories } from "@/mocks/movies";

function getCategoryName(slug: string): string {
  const cat = categories.find((c) => c.slug === slug);
  return cat ? cat.name : slug;
}

export default function MovieDetail() {
  const { movieSlug } = useParams<{ movieSlug: string }>();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [movieSlug]);

  const movie = movies.find((m) => m.slug === movieSlug);

  if (!movie) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <div className="pt-20 flex items-center justify-center h-[60vh]">
          <div className="text-center">
            <i className="ri-error-warning-line text-6xl text-foreground-400 mb-4 block"></i>
            <p className="text-foreground-500 text-lg">影片未找到</p>
            <Link to="/" className="text-primary-500 hover:text-primary-400 text-sm mt-4 inline-block cursor-pointer">
              返回首页
            </Link>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  const relatedMovies = movies
    .filter((m) => m.category === movie.category && m.id !== movie.id)
    .slice(0, 4);

  const allRelated = movies
    .filter((m) => m.id !== movie.id)
    .sort((a, b) => b.rating - a.rating)
    .slice(0, 4);

  const hasRelated = relatedMovies.length > 0;

  const movieSchema = {
    "@context": "https://schema.org",
    "@type": "Movie",
    name: movie.title,
    description: movie.description,
    director: {
      "@type": "Person",
      name: movie.director,
    },
    actor: movie.actors.map((actor) => ({
      "@type": "Person",
      name: actor,
    })),
    dateCreated: `${movie.year}`,
    countryOfOrigin: {
      "@type": "Country",
      name: movie.region,
    },
  };

  const seoTitle = `${movie.title} - 春夜影院蓝光高清在线观看`;
  const seoDescription = `${movie.title}，${movie.director}执导，${movie.actors.join("、")}主演。${movie.description.substring(0, 80)}... 春夜影院免费高清在线观看，蓝光画质流畅播放。`;
  const seoKeywords = `春夜影院,${movie.title},${movie.category === "tvseries" ? "电视剧" : "电影"},在线观看,蓝光画质,${movie.director},${movie.actors.join(",")}`;

  return (
    <>
      <SeoHead
        title={seoTitle}
        description={seoDescription}
        keywords={seoKeywords}
        canonicalUrl={`https://vxrboyl.cn/movie/${movie.slug}`}
        ogType="video.movie"
        ogImage={movie.posterUrl}
        schema={movieSchema}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-20">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6 py-6">
            <Breadcrumb
              items={[
                { label: "首页", href: "/" },
                { label: getCategoryName(movie.category), href: `/category/${movie.category}` },
                { label: movie.title },
              ]}
            />
          </div>

          {/* Movie Hero */}
          <div className="relative">
            <div className="bg-background-950">
              <div className="max-w-[1400px] mx-auto px-4 md:px-6 py-10">
                <div className="flex flex-col md:flex-row gap-8">
                  {/* Poster */}
                  <div className="flex-shrink-0 mx-auto md:mx-0">
                    <div className="w-[240px] aspect-[2/3] rounded-lg overflow-hidden">
                      <img
                        src={movie.posterUrl}
                        alt={movie.title}
                        title={`${movie.title} - 春夜影院蓝光高清在线观看`}
                        className="w-full h-full object-cover object-top"
                      />
                    </div>
                  </div>

                  {/* Info */}
                  <div className="flex-1 text-center md:text-left">
                    <h1 className="text-3xl md:text-4xl font-bold text-foreground-50 mb-3" style={{ fontFamily: "var(--font-heading)" }}>
                      {movie.title}
                    </h1>

                    <div className="flex flex-wrap items-center justify-center md:justify-start gap-3 mb-4">
                      <span className="bg-accent-500/20 text-accent-500 text-sm font-bold px-3 py-1 rounded-md">
                        {movie.rating} 分
                      </span>
                      <span className="text-foreground-400 text-sm">{movie.year}</span>
                      <span className="text-foreground-400 text-sm">{movie.region}</span>
                      <span className="text-foreground-400 text-sm">{movie.duration}</span>
                      <span className="bg-background-800 text-foreground-300 text-xs px-3 py-1 rounded-md">
                        {getCategoryName(movie.category)}
                      </span>
                    </div>

                    <p className="text-foreground-300 text-sm leading-relaxed mb-6 max-w-[600px] mx-auto md:mx-0">
                      {movie.description}
                    </p>

                    <div className="space-y-2 mb-6 text-sm">
                      <p className="text-foreground-500">
                        <span className="text-foreground-400">导演：</span>
                        <span className="text-foreground-300">{movie.director}</span>
                      </p>
                      <p className="text-foreground-500">
                        <span className="text-foreground-400">主演：</span>
                        <span className="text-foreground-300">{movie.actors.join(" / ")}</span>
                      </p>
                      <p className="text-foreground-500">
                        <span className="text-foreground-400">地区：</span>
                        <span className="text-foreground-300">{movie.region}</span>
                      </p>
                      <p className="text-foreground-500">
                        <span className="text-foreground-400">时长：</span>
                        <span className="text-foreground-300">{movie.duration}</span>
                      </p>
                    </div>

                    <Link
                      to={`/category/${movie.category}`}
                      className="inline-flex items-center px-6 py-3 bg-primary-500 hover:bg-primary-600 text-foreground-50 font-semibold rounded-full transition-all duration-300 whitespace-nowrap cursor-pointer text-sm hover:scale-105"
                    >
                      <i className="ri-play-circle-line mr-2 text-lg"></i>立即播放
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Related Movies */}
          <section className="py-12 bg-background-50">
            <div className="max-w-[1400px] mx-auto px-4 md:px-6">
              <div className="flex items-center justify-between mb-8">
                <h2 className="text-xl font-bold text-foreground-950" style={{ fontFamily: "var(--font-heading)" }}>
                  {hasRelated ? `同类${getCategoryName(movie.category)}推荐` : "高分推荐"}
                </h2>
                <Link
                  to={`/category/${movie.category}`}
                  className="text-primary-500 hover:text-primary-400 text-sm font-medium transition-colors whitespace-nowrap cursor-pointer"
                >
                  更多推荐 <i className="ri-arrow-right-line ml-1"></i>
                </Link>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-5">
                {(hasRelated ? relatedMovies : allRelated).map((m) => (
                  <MovieCard
                    key={m.id}
                    id={m.id}
                    title={m.title}
                    slug={m.slug}
                    year={m.year}
                    rating={m.rating}
                    categoryName={getCategoryName(m.category)}
                    posterUrl={m.posterUrl}
                  />
                ))}
              </div>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}